import { MemberScores } from "./types";
import { Card } from "react-bootstrap";
import { forwardRef, useMemo, useState } from "react";
import SectionHeader from "./SectionHeader";
import EmptyTeamAverageIllustration from "resources/images/team-average-empty.jpg";
import {
  RADAR_LABEL_ARRAY,
  SINGLE_POPULATION_RADAR_CHART_DATASET,
  RADAR_MEMBER_COMPARE_COLORS,
  getMemberCompareRadarChartDataset,
  hexToRgba,
} from "app/storybookComponents/Charts/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { getTraitNameAndIcon } from "./helper-functions";
import RadarChart from "app/storybookComponents/Charts/RadarChart";
import SingleUserSelect from "app/storybookComponents/SearchableInput/SingleUserSelect";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";
import useCollapsibleSection from "./useCollapsibleSection";

interface Props {
  readonly teamPersonalityScores: MemberScores | null;
  readonly teamId: number;
  readonly forceExpanded?: boolean;
}

type RadarLabel = (typeof RADAR_LABEL_ARRAY)[number];
type RadarData = { [key in RadarLabel]?: number };

const TRAIT_ABBREVIATIONS = [
  "ACH",
  "MOT",
  "CMP",
  "AST",
  "EXT",
  "COP",
  "PAT",
  "SCN",
  "CON",
  "OPEN",
] as const;

type TraitAbbrev = (typeof TRAIT_ABBREVIATIONS)[number];

const MAX_COMPARED_MEMBERS = 8;

const buildRadarData = (scores: Partial<Record<TraitAbbrev, number>>) => {
  const radarData: RadarData = {};
  TRAIT_ABBREVIATIONS.forEach((abbrev) => {
    const trait = getTraitNameAndIcon(abbrev);
    if (!trait || !RADAR_LABEL_ARRAY.includes(trait.traitName)) return;
    radarData[trait.traitName as RadarLabel] = scores[abbrev];
  });
  return RADAR_LABEL_ARRAY.map((label) => (radarData[label] ?? 0) + 25);
};

const RadarChartCard = forwardRef<HTMLDivElement, Props>(
  function RadarChartCard(
    { teamPersonalityScores, teamId, forceExpanded },
    ref
  ) {
    const { collapsed, toggleIsCollapsed } =
      useCollapsibleSection(forceExpanded);
    const usersById = useAppSelector(selectAllCompanyUsersById);
    const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
    const [comparedMembers, setComparedMembers] = useState<
      { userAccountId: number; colorIndex: number }[]
    >([]);

    const addComparedMember = (userAccountId: number) => {
      setComparedMembers((members) => {
        if (members.some((member) => member.userAccountId === userAccountId))
          return members;
        if (members.length >= MAX_COMPARED_MEMBERS) return members;
        const usedColorIndexes = new Set(members.map((m) => m.colorIndex));
        let colorIndex = 0;
        while (usedColorIndexes.has(colorIndex)) colorIndex += 1;
        return [...members, { userAccountId, colorIndex }];
      });
    };

    const removeComparedMember = (userAccountId: number) => {
      setComparedMembers((members) =>
        members.filter((m) => m.userAccountId !== userAccountId)
      );
    };

    const getComparedUser = ({
      userAccountId,
      colorIndex,
    }: {
      userAccountId: number;
      colorIndex: number;
    }) => {
      const user = usersById[userAccountId] ?? sampleUsersById[userAccountId];
      const userName = user?.firstName
        ? `${user.firstName} ${user?.lastName ?? ""}`
        : user?.emailAddress || `UserID: ${userAccountId}`;
      const color = RADAR_MEMBER_COMPARE_COLORS[colorIndex];
      return (
        <div
          key={userAccountId}
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm"
          style={{
            borderColor: color,
            backgroundColor: hexToRgba(color, 0.08),
          }}
        >
          <span
            className="h-2 w-2 flex-shrink-0 rounded-full"
            style={{ backgroundColor: color }}
          />
          {userName}
          <button
            className="no-style-button flex items-center"
            type="button"
            aria-label={`Remove ${userName} from comparison`}
            onClick={() => removeComparedMember(userAccountId)}
          >
            <FontAwesomeIcon icon={faXmark} size="xs" />
          </button>
        </div>
      );
    };

    const getMemberComparePicker = () => {
      const teamMembers = teamPersonalityScores?.teamMembers || [];
      if (!teamMembers.length) return null;

      const comparedMemberIds = comparedMembers.map((m) => m.userAccountId);
      const isMaxedOut = comparedMembers.length >= MAX_COMPARED_MEMBERS;

      return (
        <div style={{ marginBottom: "16px" }}>
          <div className="flex flex-wrap gap-2 mt-2">
            {isMaxedOut ? null : (
              <div className="max-w-[250px]">
                <SingleUserSelect
                  users={{ ...sampleUsersById, ...usersById }}
                  setSelectedValue={(userAccountId) => {
                    if (
                      !userAccountId ||
                      comparedMemberIds.includes(userAccountId)
                    ) {
                      return;
                    }
                    addComparedMember(userAccountId);
                  }}
                  userAccountIds={teamMembers
                    .filter(
                      (tm) => !comparedMemberIds.includes(tm.userAccountId)
                    )
                    .map((tm) => tm.userAccountId)}
                  className="mb-0"
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2 h-fit align-self-center">
              {comparedMembers.map(getComparedUser)}
            </div>
          </div>
          {isMaxedOut ? (
            <p
              className="grey-text"
              style={{ fontSize: "13px", margin: "6px 0 0" }}
            >
              You can compare up to {MAX_COMPARED_MEMBERS} team members at a
              time. Remove one to add another.
            </p>
          ) : null}
        </div>
      );
    };

    // Memoized so unrelated re-renders (e.g. PDF export state elsewhere on
    // the page) don't create a new dataSets reference, which would otherwise
    // cause RadarChart to tear down and rebuild the whole Chart.js instance.
    const radarChartDataSets = useMemo(() => {
      if (!teamPersonalityScores?.teamScores) {
        return [];
      }

      const teamAverages: Partial<Record<TraitAbbrev, number>> = {};
      Object.entries(teamPersonalityScores.teamScores).forEach(
        ([key, value]) => {
          teamAverages[key as TraitAbbrev] = value.teamAverage;
        }
      );

      const teamAverageDataSet = {
        ...SINGLE_POPULATION_RADAR_CHART_DATASET,
        data: buildRadarData(teamAverages),
        label: "Team Average",
      };

      const memberDataSets = forceExpanded
        ? []
        : comparedMembers
            .map(({ userAccountId, colorIndex }) => {
              const member = teamPersonalityScores?.teamMembers?.find(
                (teamMember) => teamMember.userAccountId === userAccountId
              );
              if (!member) return null;

              return {
                ...getMemberCompareRadarChartDataset(
                  RADAR_MEMBER_COMPARE_COLORS[colorIndex]
                ),
                data: buildRadarData(member),
                label: `${member.firstName} ${member.lastName}`,
              };
            })
            .filter(
              (dataSet): dataSet is NonNullable<typeof dataSet> =>
                dataSet !== null
            );

      return [teamAverageDataSet, ...memberDataSets];
    }, [teamPersonalityScores, comparedMembers, forceExpanded]);

    const getRadarChart = () => {
      if (teamPersonalityScores === null) {
        return (
          <div className="column-gap-20px align-items-center">
            <img
              src={EmptyTeamAverageIllustration}
              alt="empty team average illustration"
              style={{
                width: "200px",
                height: "200px",
              }}
            />
            <p
              style={{
                maxWidth: "600px",
                textAlign: "center",
                color: "#5E6165",
              }}
            >
              Once your team members have taken their personality assessment or
              connected their results, your team personality summary will be
              shown here.
            </p>
          </div>
        );
      }

      return (
        <>
          {!forceExpanded && getMemberComparePicker()}
          <RadarChart
            canvasId={`team-personality-radar-chart-${teamId}`}
            dataSets={radarChartDataSets}
            includeDashedBaseline
            includeGeneralPopulation
          />
        </>
      );
    };

    if (!teamPersonalityScores) {
      return (
        <Card ref={ref}>
          <h2>Team Personality</h2>
          <p style={{ marginTop: "10px", marginBottom: "10px" }}>
            The Workplace Insights report shows how your team members work
            according to 10 different behavioral traits. Send the EPP assessment
            to your team members to see results.
          </p>
          {getRadarChart()}
        </Card>
      );
    }

    if (collapsed) {
      return (
        <Card ref={ref}>
          <SectionHeader
            toggleIsCollapsed={toggleIsCollapsed}
            title="Team Personality"
            isCollapsed={collapsed}
          />
        </Card>
      );
    }

    return (
      <Card ref={ref}>
        <SectionHeader
          toggleIsCollapsed={toggleIsCollapsed}
          title="Team Personality"
          isCollapsed={collapsed}
          hideActionButton={forceExpanded}
        />
        <p className="team-personality-paragraph">
          See where your team falls on average for all traits measured in your
          Team Personality, or compare up to {MAX_COMPARED_MEMBERS} individual
          team members against the team average.
        </p>
        {getRadarChart()}
      </Card>
    );
  }
);

export default RadarChartCard;
